/**
 * Landing for a signed-in user who belongs to an organization but has
 * no workspace access yet.
 *
 * The common way to reach this state: accepting an org invitation.
 * Invitations grant ORGANIZATION membership only — workspace access
 * is a separate grant an admin makes (ADR 0010's two-tier model). So
 * a freshly-invited member is in the org but can't open any workspace.
 *
 * `IdentityGate` routes such users here instead of letting
 * `WorkspaceRedirect` / `useAppContext` crash trying to resolve a
 * workspace they'd 403 on. The screen is honest about what's needed
 * (an admin grant) and offers a Refresh once that's done.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, RefreshCw, LogOut } from 'lucide-react'

import { Button } from '@/components/ui'
import { AuthLayout } from '@/auth/AuthLayout'
import { useIdentity } from '@/hooks/useIdentity'
import { authClient } from '@/auth/authClient'
import { clearSuperadminMode } from '@/auth/superadminMode'

export function NoWorkspaceAccess() {
  const { state, refreshIdentity } = useIdentity()
  const navigate = useNavigate()
  const [refreshing, setRefreshing] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const orgName =
    state.status === 'ready' && state.identity.orgs[0]
      ? state.identity.orgs[0].name
      : 'your organization'

  async function handleRefresh() {
    setRefreshing(true)
    try {
      // Re-fetches /api/me. If an admin has granted workspace access
      // since, the refreshed identity carries a non-empty
      // workspaceRoles map and IdentityGate stops routing here.
      await refreshIdentity()
    } finally {
      setRefreshing(false)
    }
  }

  async function handleSignOut() {
    setSigningOut(true)
    // Clear the sessionStorage-backed superadmin-mode toggle BEFORE the
    // network call — same as the Header sign-out path. Without this the
    // next session on this tab could inherit a stale
    // `x-superadmin-mode: on` header.
    clearSuperadminMode()
    try {
      await authClient.signOut()
    } catch {
      // Navigate away regardless — the cookie may already be gone and
      // the login screen surfaces a real error if sign-in then fails.
    }
    navigate('/auth/login', { replace: true })
  }

  return (
    <AuthLayout
      title="No workspace yet"
      subtitle={`You've joined ${orgName}, but you don't have access to a workspace.`}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm">
          <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-muted-foreground">
            An admin needs to grant you access to a workspace before you
            can start. Ask an admin of {orgName} to add you to one, then
            refresh.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => void handleRefresh()} disabled={refreshing} className="flex-1">
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {refreshing ? 'Checking…' : 'Refresh'}
          </Button>
          <Button variant="outline" onClick={() => void handleSignOut()} disabled={signingOut}>
            <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
